import { useEffect, useState } from 'react'
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react'
import { getTour, saveTour, type AdminTour } from '../lib/api'

/**
 * Der geführte Rundgang: In welcher Reihenfolge K4ia die Stände zeigt und wie
 * der Besucher von einem zum nächsten kommt — „Dreh dich nach links, lauf zur
 * Glasvitrine …". K4ia gibt die Wegbeschreibung in ihren Worten wieder,
 * erfindet aber nichts dazu; was hier fehlt, sagt sie nicht.
 *
 * Reihenfolge per Pfeil, weil das am Handy verlässlicher ist als Ziehen. Die
 * erste Wegbeschreibung führt vom Eingang zur ersten Station.
 */
interface Station {
  showcaseId: string
  directions: string
}

export function TourPage() {
  const [data, setData] = useState<AdminTour | null>(null)
  const [stationen, setStationen] = useState<Station[]>([])
  const [neu, setNeu] = useState('')
  const [meldung, setMeldung] = useState<{ art: 'ok' | 'fehler'; text: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [geaendert, setGeaendert] = useState(false)

  useEffect(() => {
    void (async () => {
      const d = await getTour()
      setData(d)
      setStationen(d.stops.map((s) => ({ showcaseId: s.showcaseId, directions: s.directions })))
    })()
  }, [])

  const titel = (id: string) => data?.showcases.find((s) => s.id === id)?.title ?? id
  const status = (id: string) => data?.showcases.find((s) => s.id === id)?.status
  const uebrig = (data?.showcases ?? []).filter(
    (s) => !stationen.some((st) => st.showcaseId === s.id),
  )

  function aendern(next: Station[]) {
    setStationen(next)
    setGeaendert(true)
    setMeldung(null)
  }

  function verschieben(i: number, richtung: -1 | 1) {
    const j = i + richtung
    if (j < 0 || j >= stationen.length) return
    const next = [...stationen]
    const [a] = next.splice(i, 1)
    next.splice(j, 0, a!)
    aendern(next)
  }

  function hinzufuegen() {
    if (!neu) return
    aendern([...stationen, { showcaseId: neu, directions: '' }])
    setNeu('')
  }

  async function speichern() {
    setBusy(true)
    setMeldung(null)
    try {
      const { stops } = await saveTour({ stops: stationen })
      setStationen(stops.map((s) => ({ showcaseId: s.showcaseId, directions: s.directions })))
      setGeaendert(false)
      setMeldung({ art: 'ok', text: 'Gespeichert. K4ia führt ab sofort in dieser Reihenfolge.' })
    } catch (err) {
      setMeldung({ art: 'fehler', text: err instanceof Error ? err.message : String(err) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="max-w-3xl space-y-8">
      <div className="space-y-2">
        <h2 className="page-title">Rundgang</h2>
        <p className="text-sm leading-relaxed text-text-muted">
          „Führ mich durchs Lab“: K4ia geht die Stände in dieser Reihenfolge durch. Zu jeder Station
          schreibst du, wie der Besucher von der vorigen dorthin kommt — bei der ersten vom Eingang
          aus. K4ia sagt erst den Weg, wartet auf „Ich bin da“ und erklärt dann. Ohne
          Wegbeschreibung nennt sie nur den Stand.
        </p>
      </div>

      {meldung && (
        <p
          role={meldung.art === 'fehler' ? 'alert' : 'status'}
          className={`text-sm ${meldung.art === 'fehler' ? 'text-status-error' : 'text-text-muted'}`}
        >
          {meldung.text}
        </p>
      )}

      {stationen.length === 0 && (
        <p className="card p-5 text-sm text-text-muted">
          Noch keine Stationen. Solange der Rundgang leer ist, führt K4ia in der Sortierreihenfolge
          der Showcases — ohne Wegbeschreibung.
        </p>
      )}

      <ol className="space-y-3">
        {stationen.map((st, i) => (
          <li key={st.showcaseId} className="card space-y-3 p-4 sm:p-5">
            <div className="flex items-center gap-1 sm:gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-2 font-mono text-sm">
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{titel(st.showcaseId)}</p>
                {status(st.showcaseId) !== 'live' && (
                  <p className="text-xs text-status-error">nicht live — wird übersprungen</p>
                )}
              </div>
              <button
                type="button"
                aria-label="Nach oben"
                disabled={i === 0}
                onClick={() => verschieben(i, -1)}
                className="flex h-11 w-11 items-center justify-center rounded-full text-text-muted hover:text-text disabled:opacity-30"
              >
                <ArrowUp size={18} />
              </button>
              <button
                type="button"
                aria-label="Nach unten"
                disabled={i === stationen.length - 1}
                onClick={() => verschieben(i, 1)}
                className="flex h-11 w-11 items-center justify-center rounded-full text-text-muted hover:text-text disabled:opacity-30"
              >
                <ArrowDown size={18} />
              </button>
              <button
                type="button"
                aria-label="Aus dem Rundgang nehmen"
                onClick={() => aendern(stationen.filter((_, k) => k !== i))}
                className="flex h-11 w-11 items-center justify-center rounded-full text-text-muted hover:text-status-error"
              >
                <X size={18} />
              </button>
            </div>
            <label className="block">
              <span className="mb-1 block text-xs text-text-muted">
                {i === 0
                  ? 'Weg vom Eingang hierher'
                  : `Weg von „${titel(stationen[i - 1]!.showcaseId)}“ hierher`}
              </span>
              <textarea
                className="input min-h-[4.5rem]"
                placeholder={
                  i === 0
                    ? 'z. B. Vom Eingang aus geradeaus, der große Bildschirm links vor dem Schaufenster.'
                    : 'z. B. Dreh dich nach links und lauf bis zur Glasvitrine an der Wand.'
                }
                value={st.directions}
                maxLength={1000}
                onChange={(e) =>
                  aendern(
                    stationen.map((s, k) => (k === i ? { ...s, directions: e.target.value } : s)),
                  )
                }
              />
            </label>
          </li>
        ))}
      </ol>

      {uebrig.length > 0 && (
        <div className="card flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:p-5">
          <select className="input sm:flex-1" value={neu} onChange={(e) => setNeu(e.target.value)}>
            <option value="">Station hinzufügen …</option>
            {uebrig.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
                {s.status !== 'live' ? ' (nicht live)' : ''}
              </option>
            ))}
          </select>
          <button type="button" onClick={hinzufuegen} disabled={!neu} className="btn-secondary">
            <Plus size={16} /> Hinzufügen
          </button>
        </div>
      )}

      <div className="sticky-actions flex items-center gap-3 rounded-full max-md:glass max-md:p-2 max-md:shadow-[var(--shadow-card)]">
        <button
          type="button"
          onClick={() => void speichern()}
          disabled={busy || !geaendert}
          className="btn-primary max-md:flex-1"
        >
          {busy ? 'Speichert …' : 'Rundgang speichern'}
        </button>
        {geaendert && (
          <span className="text-xs text-text-muted">Änderungen noch nicht gespeichert</span>
        )}
      </div>
    </div>
  )
}
